import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import {
  BarChart,
  Bar,
  LineChart,
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  Cell,
  PieChart,
  Pie
} from 'recharts';
import { RefreshCw, LogOut, ChevronDown } from 'lucide-react';
import { Navigation } from './Navigation';

// Format number with Danish locale
const formatDKK = (value) => {
  if (value === null || value === undefined) return '-';
  return Math.round(value).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
};

// Format percentage
const formatPct = (value) => {
  if (value === null || value === undefined) return '-';
  return `${value.toFixed(1)}%`;
};

// Speedometer/Gauge Component
const SpeedometerGauge = ({ title, value, min = 0, max = 100, unit = '', thresholds, subtitle, avgValue }) => {
  const percentage = Math.min(Math.max((value - min) / (max - min) * 100, 0), 100);
  const angle = (percentage / 100) * 180;
  
  const getColor = () => {
    if (!thresholds) return '#1E6F7A';
    if (value >= thresholds.good) return '#2E7D32';
    if (value >= thresholds.warning) return '#C77700';
    return '#B3261E';
  };
  const color = getColor();
  
  // Threshold angles for colored segments
  const warningPct = thresholds ? ((thresholds.warning - min) / (max - min)) * 180 : 60;
  const goodPct = thresholds ? ((thresholds.good - min) / (max - min)) * 180 : 120;
  
  // Average marker angle
  const avgAngle = avgValue != null ? Math.min(Math.max(((avgValue - min) / (max - min)) * 180, 0), 180) : null;
  
  const radius = 45;
  const sw = 8;
  const cx = 50, cy = 55;
  
  const polarToCartesian = (r, ang) => {
    const rad = (ang - 180) * Math.PI / 180;
    return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
  };
  const describeArc = (startAngle, endAngle) => {
    const s = polarToCartesian(radius, endAngle);
    const e = polarToCartesian(radius, startAngle);
    const large = endAngle - startAngle <= 180 ? "0" : "1";
    return `M ${s.x} ${s.y} A ${radius} ${radius} 0 ${large} 0 ${e.x} ${e.y}`;
  };
  
  const needleEnd = polarToCartesian(radius - 10, angle);
  const displayVal = typeof value === 'number' ? (value < 10 ? value.toFixed(2) : formatDKK(value)) : '-';
  const avgDisplayVal = avgValue != null ? (avgValue < 10 ? avgValue.toFixed(2) : formatDKK(avgValue)) : null;
  
  return (
    <div className="flex flex-col items-center">
      <p className="text-xs text-white/60 mb-1 text-center">{title}</p>
      <svg viewBox="-5 -5 110 75" className="w-full max-w-[160px]">
        <path d={describeArc(0, 180)} fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth={sw} strokeLinecap="round" />
        <path d={describeArc(goodPct, 180)} fill="none" stroke="#2E7D32" strokeWidth={sw} strokeLinecap="round" opacity="0.25" />
        <path d={describeArc(warningPct, goodPct)} fill="none" stroke="#C77700" strokeWidth={sw} strokeLinecap="round" opacity="0.25" />
        <path d={describeArc(0, warningPct)} fill="none" stroke="#B3261E" strokeWidth={sw} strokeLinecap="round" opacity="0.25" />
        {angle > 0 && (
          <path d={describeArc(0, Math.min(angle, 180))} fill="none" stroke={color} strokeWidth={sw} strokeLinecap="round" />
        )}
        {/* Average marker on the arc */}
        {avgAngle != null && (() => {
          const outer = polarToCartesian(radius + 8, avgAngle);
          const inner = polarToCartesian(radius - 8, avgAngle);
          const labelPos = polarToCartesian(radius - 20, avgAngle);
          return (
            <>
              <line x1={inner.x} y1={inner.y} x2={outer.x} y2={outer.y} stroke="#ffffff" strokeWidth="3" strokeLinecap="round" />
              <text x={labelPos.x} y={labelPos.y} fill="#ffffff" fontSize="8" fontWeight="700" textAnchor="middle" dominantBaseline="middle" style={{ textShadow: '0 0 3px rgba(0,0,0,0.8)' }}>Gns</text>
            </>
          );
        })()}
        <line x1={cx} y1={cy} x2={needleEnd.x} y2={needleEnd.y} stroke={color} strokeWidth="2" strokeLinecap="round" />
        <circle cx={cx} cy={cy} r="4" fill={color} />
      </svg>
      <p className="text-xl font-bold -mt-1" style={{ color }}>{displayVal}</p>
      {subtitle && <p className="text-xs text-white/40 text-center">{subtitle}</p>}
      <div className="flex justify-between w-full text-xs text-white/30 mt-0.5 px-1">
        <span>{min}</span>
        <span>{max}</span>
      </div>
    </div>
  );
};

// Period options
// Summary Card Component
const SummaryCard = ({ title, value, unit = '', color = '#1E6F7A' }) => (
  <div className="bg-[#1A1A1A] rounded-lg p-3 flex flex-col min-w-[100px]">
    <p className="text-xs text-white/60 truncate">{title}</p>
    <p className="text-lg font-bold mt-1" style={{ color }}>
      {value}{unit && <span className="text-sm font-normal text-white/40 ml-1">{unit}</span>}
    </p>
  </div>
);

// Metric Chart Component
const MetricChart = React.memo(({ title, data, dataKey, color = '#1E6F7A', unit = '', formatter, chartType = 'bar' }) => {
  if (!data || data.length === 0) {
    return (
      <div className="bg-[#1A1A1A] rounded-lg p-4">
        <h4 className="text-sm font-medium text-white/80 mb-3">{title}</h4>
        <div className="h-[150px] flex items-center justify-center">
          <p className="text-sm text-white/40">Ingen data</p>
        </div>
      </div>
    );
  }

  const axisProps = {
    x: {
      dataKey: "month_label",
      stroke: "rgba(255,255,255,0.4)",
      tick: { fill: 'rgba(255,255,255,0.6)', fontSize: 10 },
      interval: 0,
      angle: -45,
      textAnchor: "end",
      height: 50,
    },
    y: {
      stroke: "rgba(255,255,255,0.4)",
      tick: { fill: 'rgba(255,255,255,0.6)', fontSize: 10 },
      tickFormatter: formatter || ((v) => v >= 1000000 ? `${(v/1000000).toFixed(1)}M` : v >= 1000 ? `${(v/1000).toFixed(0)}k` : v),
      width: 45,
    },
  };

  const tooltipProps = {
    contentStyle: { backgroundColor: '#1A1A1A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' },
    formatter: (value) => [formatter ? formatter(value) : `${formatDKK(value)}${unit}`, title],
    labelFormatter: (label) => label,
  };

  return (
    <div className="bg-[#1A1A1A] rounded-lg p-4">
      <h4 className="text-sm font-medium text-white/80 mb-3">{title}</h4>
      <div className="h-[150px]">
        <ResponsiveContainer width="100%" height="100%">
          {chartType === 'line' ? (
            <LineChart data={data} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
              <XAxis {...axisProps.x} />
              <YAxis {...axisProps.y} />
              <Tooltip {...tooltipProps} />
              <Line type="monotone" dataKey={dataKey} stroke={color} strokeWidth={2} dot={{ fill: color, r: 3 }} activeDot={{ r: 5 }} />
            </LineChart>
          ) : (
            <BarChart data={data} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
              <XAxis {...axisProps.x} />
              <YAxis {...axisProps.y} />
              <Tooltip {...tooltipProps} />
              <Bar dataKey={dataKey} fill={color} radius={[4, 4, 0, 0]} />
            </BarChart>
          )}
        </ResponsiveContainer>
      </div>
    </div>
  );
});

export const Konsulenter = ({ user }) => {
  const navigate = useNavigate();
  const [refreshing, setRefreshing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('salgstal');
  
  // Filters
  const [consultants, setConsultants] = useState([]);
  const [selectedConsultant, setSelectedConsultant] = useState('all');
  
  // Date range - default to last 12 weeks (same approach as historical)
  const today = new Date().toISOString().split('T')[0];
  const twelveWeeksAgo = new Date(Date.now() - 84 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
  const [startDate, setStartDate] = useState(twelveWeeksAgo);
  const [endDate, setEndDate] = useState(today);
  
  // Data
  const [consultantData, setConsultantData] = useState(null);
  const [allAvgData, setAllAvgData] = useState(null);
  const [tidsfordelingData, setTidsfordelingData] = useState(null);
  const [ledigeTimerData, setLedigeTimerData] = useState(null);
  const [fravaerData, setFravaerData] = useState(null);

  // Fetch consultants list
  const fetchConsultants = useCallback(async () => {
    try {
      const response = await api.get('/konsulenter/list');
      setConsultants(response.data.consultants || []);
    } catch (err) {
      console.error('Error fetching consultants:', err);
      if (err.response?.status === 401) {
        navigate('/login');
      }
    }
  }, [navigate]);

  // Fetch consultant data
  const fetchConsultantData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        email: selectedConsultant,
        start_date: startDate,
        end_date: endDate,
        group_by: 'month'
      };
      
      // If a specific consultant is selected, also fetch "all" for averages
      if (selectedConsultant !== 'all') {
        const [res, avgRes, tidsRes, ledigeRes, fravaerRes] = await Promise.all([
          api.get('/konsulenter/data', { params }),
          api.get('/konsulenter/data', { params: { ...params, email: 'all' } }),
          api.get('/konsulenter/tidsfordeling', { params }),
          api.get('/konsulenter/ledige-timer', { params }),
          api.get('/konsulenter/fravaer', { params })
        ]);
        setConsultantData(res.data);
        setAllAvgData(avgRes.data?.summary || null);
        setTidsfordelingData(tidsRes.data);
        setLedigeTimerData(ledigeRes.data);
        setFravaerData(fravaerRes.data);
      } else {
        const [res, tidsRes, ledigeRes, fravaerRes] = await Promise.all([
          api.get('/konsulenter/data', { params }),
          api.get('/konsulenter/tidsfordeling', { params }),
          api.get('/konsulenter/ledige-timer', { params }),
          api.get('/konsulenter/fravaer', { params })
        ]);
        setConsultantData(res.data);
        setAllAvgData(null);
        setTidsfordelingData(tidsRes.data);
        setLedigeTimerData(ledigeRes.data);
        setFravaerData(fravaerRes.data);
      }
    } catch (err) {
      console.error('Error fetching consultant data:', err);
      if (err.response?.status === 401) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  }, [selectedConsultant, startDate, endDate, navigate]);

  // Initial load
  useEffect(() => {
    fetchConsultants();
  }, [fetchConsultants]);

  // Fetch data when filters change
  useEffect(() => {
    fetchConsultantData();
  }, [fetchConsultantData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchConsultantData();
    setRefreshing(false);
  };

  const handleLogout = async () => {
    try {
      await api.post('/auth/logout');
      navigate('/login');
    } catch (err) {
      navigate('/login');
    }
  };

  // Summary data
  const summary = consultantData?.summary || {};
  const periodData = consultantData?.period_data || [];

  // Tab content
  const renderTabContent = () => {
    if (activeTab === 'salgstal') {
      return (
        <div className="space-y-6">
          {/* KPI Speedometers */}
          <div className="bg-[#151515] rounded-xl p-5 border border-white/5" data-testid="kpi-speedometers">
            <h3 className="text-sm font-medium text-white/80 mb-4">Nøgletal</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <SpeedometerGauge 
                title="DPT" 
                subtitle="Dækning pr. time"
                value={summary.dpt || 0} 
                min={0} 
                max={3000} 
                unit="kr"
                thresholds={{ good: 2000, warning: 1500 }}
                avgValue={allAvgData?.dpt}
              />
              <SpeedometerGauge 
                title="DPB" 
                subtitle="Dækning pr. booking"
                value={summary.dpk || summary.dpb || 0} 
                min={0} 
                max={8000} 
                unit="kr"
                thresholds={{ good: 4800, warning: 4000 }}
                avgValue={allAvgData?.dpk || allAvgData?.dpb}
              />
              <SpeedometerGauge 
                title="KPT" 
                subtitle="Kunder pr. time"
                value={summary.kpt || 0} 
                min={0} 
                max={0.6} 
                unit=""
                thresholds={{ good: 0.42, warning: 0.40 }}
                avgValue={allAvgData?.kpt}
              />
              <SpeedometerGauge 
                title="KP" 
                subtitle="Konverteringsprocent"
                value={summary.konvertering || 0} 
                min={0} 
                max={100} 
                unit="%"
                thresholds={{ good: 70, warning: 50 }}
                avgValue={allAvgData?.konvertering}
              />
            </div>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-11 gap-3 pb-2">
            <SummaryCard title="Omsætning" value={formatDKK(summary.omsaetning)} unit="kr" />
            <SummaryCard title="DB Produkt" value={formatDKK(summary.db_produkt)} unit="kr" />
            <SummaryCard title="DB Montering" value={formatDKK(summary.db_mont)} unit="kr" />
            <SummaryCard title="DB i alt" value={formatDKK(summary.db_samlet)} unit="kr" />
            <SummaryCard title="DG" value={formatPct(summary.dg)} color={summary.dg >= 30 ? '#2E7D32' : summary.dg >= 20 ? '#C77700' : '#B3261E'} />
            <SummaryCard title="DPT" value={formatDKK(summary.dpt)} unit="kr" />
            <SummaryCard title="DPB" value={formatDKK(summary.dpk || summary.dpb)} unit="kr" />
            <SummaryCard title="KPT" value={summary.kpt?.toFixed(2) || '-'} color={summary.kpt >= 0.42 ? '#2E7D32' : summary.kpt >= 0.40 ? '#C77700' : '#B3261E'} />
            <SummaryCard title="Konvertering" value={formatPct(summary.konvertering)} color={summary.konvertering >= 70 ? '#2E7D32' : summary.konvertering >= 50 ? '#C77700' : '#B3261E'} />
            <SummaryCard title="Timer" value={summary.real_business_hours?.toFixed(0) || '0'} unit="t" />
            <SummaryCard title="Ordrer" value={summary.igangvaerende || 0} />
          </div>

          {/* Lukketid sektion */}
          <div className="bg-[#151515] rounded-xl p-5 border border-white/5">
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-sm font-medium text-white/80">Lukketid — fra kundebesøg til ordre</h3>
              <span className="text-xs text-white/30 italic">DEMO-DATA</span>
            </div>
            <p className="text-xs text-white/30 mb-5">Måler hvor hurtigt konsulenten lukker ordrer efter opmåling</p>
            {(() => {
              const dummyDistribution = [
                { label: 'Samme dag', count: 42, pct: 38, color: '#2E7D32' },
                { label: '1-3 dage', count: 28, pct: 25, color: '#4CAF50' },
                { label: '4-7 dage', count: 18, pct: 16, color: '#FFB300' },
                { label: '1-2 uger', count: 14, pct: 13, color: '#FF6F00' },
                { label: '2+ uger', count: 9, pct: 8, color: '#EF5350' },
              ];
              const dummyTrend = periodData?.map((p, i) => ({
                ...p,
                avg_lukketid: [4.2, 3.8, 5.1, 3.5, 2.9, 3.2, 4.0, 2.8, 3.1, 3.6, 2.5, 3.0][i % 12] || 3.5,
                samme_dag_pct: [35, 38, 32, 40, 45, 42, 36, 48, 44, 39, 50, 43][i % 12] || 40,
              })) || [];
              const avgDays = 3.6;
              const sameDayPct = 38;
              return (
                <div className="space-y-6">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-[#1A1A1A] rounded-lg p-4 text-center">
                      <p className="text-xs text-white/40 mb-1">Gns. lukketid</p>
                      <p className={`text-2xl font-bold ${avgDays <= 3 ? 'text-[#2E7D32]' : avgDays <= 7 ? 'text-[#FFB300]' : 'text-[#EF5350]'}`}>{avgDays} dage</p>
                    </div>
                    <div className="bg-[#1A1A1A] rounded-lg p-4 text-center">
                      <p className="text-xs text-white/40 mb-1">Lukket samme dag</p>
                      <p className={`text-2xl font-bold ${sameDayPct >= 50 ? 'text-[#2E7D32]' : sameDayPct >= 30 ? 'text-[#FFB300]' : 'text-[#EF5350]'}`}>{sameDayPct}%</p>
                    </div>
                    <div className="bg-[#1A1A1A] rounded-lg p-4 text-center">
                      <p className="text-xs text-white/40 mb-1">Lukket inden 7 dage</p>
                      <p className="text-2xl font-bold text-[#4CAF50]">79%</p>
                    </div>
                    <div className="bg-[#1A1A1A] rounded-lg p-4 text-center">
                      <p className="text-xs text-white/40 mb-1">Kræver opfølgning (2+ uger)</p>
                      <p className={`text-2xl font-bold ${8 <= 5 ? 'text-[#2E7D32]' : 8 <= 10 ? 'text-[#FFB300]' : 'text-[#EF5350]'}`}>8%</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-white/50 mb-2">Fordeling af lukketid</p>
                    <div className="flex rounded-lg overflow-hidden h-10">
                      {dummyDistribution.map((d, i) => (
                        <div key={i} style={{ width: `${d.pct}%`, backgroundColor: d.color }} className="flex items-center justify-center text-xs font-semibold text-white min-w-[40px] transition-all">
                          {d.pct}%
                        </div>
                      ))}
                    </div>
                    <div className="flex justify-between mt-2">
                      {dummyDistribution.map((d, i) => (
                        <div key={i} className="flex items-center gap-1">
                          <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: d.color }} />
                          <span className="text-xs text-white/40">{d.label} ({d.count})</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <MetricChart 
                      title="Gns. lukketid (dage) pr. måned" 
                      data={dummyTrend} 
                      dataKey="avg_lukketid" 
                      color="#FF6F00"
                      formatter={(v) => `${v?.toFixed(1) || 0} dage`}
                    />
                    <MetricChart 
                      title="Lukket samme dag (%) pr. måned" 
                      data={dummyTrend} 
                      dataKey="samme_dag_pct" 
                      color="#2E7D32"
                      formatter={(v) => `${v?.toFixed(0) || 0}%`}
                    />
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Charts Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            <MetricChart 
              title="Omsætning pr. måned" 
              data={periodData} 
              dataKey="omsaetning" 
              color="#1E6F7A"
              unit=" kr"
            />
            <MetricChart 
              title="DB Produkt pr. måned" 
              data={periodData} 
              dataKey="db_produkt" 
              color="#2E7D32"
              unit=" kr"
            />
            <MetricChart 
              title="DB Montering pr. måned" 
              data={periodData} 
              dataKey="db_mont" 
              color="#C77700"
              unit=" kr"
            />
            <MetricChart 
              title="DB i alt pr. måned" 
              data={periodData} 
              dataKey="db_samlet" 
              color="#1E6F7A"
              unit=" kr"
            />
            <MetricChart 
              title="DG % pr. måned" 
              data={periodData} 
              dataKey="dg" 
              color="#2E7D32"
              chartType="line"
              formatter={(v) => `${v?.toFixed(1) || 0}%`}
            />
            <MetricChart 
              title="DPT (Dækning pr. time) pr. måned" 
              data={periodData} 
              dataKey="dpt" 
              color="#1E6F7A"
              unit=" kr"
            />
            <MetricChart 
              title="DPB (Dækning pr. booking) pr. måned" 
              data={periodData} 
              dataKey="dpk" 
              color="#9C27B0"
              unit=" kr"
            />
            <MetricChart 
              title="DPO (Dækning pr. ordre) pr. måned" 
              data={periodData} 
              dataKey="dpb" 
              color="#C77700"
              unit=" kr"
            />
            <MetricChart 
              title="Antal kunder pr. måned" 
              data={periodData} 
              dataKey="real_opmaling" 
              color="#E65100"
              formatter={(v) => v?.toString() || '0'}
            />
            <MetricChart 
              title="KPT pr. måned" 
              data={periodData} 
              dataKey="kpt" 
              color="#1E6F7A"
              formatter={(v) => v?.toFixed(2) || '0'}
            />
            <MetricChart 
              title="Konvertering % pr. måned" 
              data={periodData} 
              dataKey="konvertering" 
              color="#2E7D32"
              chartType="line"
              formatter={(v) => `${v?.toFixed(1) || 0}%`}
            />
            <MetricChart 
              title="Igangværende ordrer pr. måned" 
              data={periodData} 
              dataKey="igangvaerende" 
              color="#C77700"
              formatter={(v) => v?.toString() || '0'}
            />
          </div>
        </div>
      );
    }

    if (activeTab === 'profit') {
      const dbProd = summary.db_produkt || 0;
      const dbMont = summary.db_mont || 0;
      const dbTotal = summary.db_samlet || 0;

      // Udgifter (dummy-tal undtagen provision)
      const provision = dbTotal * 0.20;
      const billeasing = 4500 * (periodData?.length || 1);
      const braendstof = 3200 * (periodData?.length || 1);
      const marketing = 8000 * (periodData?.length || 1);
      const udgifterTotal = provision + billeasing + braendstof + marketing;
      const profit = dbTotal - udgifterTotal;

      const udgiftsPoster = [
        { label: 'Provision (20% af dækning)', value: provision },
        { label: 'Billeasing', value: billeasing, dummy: true },
        { label: 'Brændstof', value: braendstof, dummy: true },
        { label: 'Marketing', value: marketing, dummy: true },
      ];

      return (
        <div className="space-y-6">
          {/* Overview cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-[#151515] rounded-xl p-5 border border-white/5">
              <p className="text-xs text-white/50 mb-1">Dækning (indtægt)</p>
              <p className="text-2xl font-bold text-[#2E7D32]">{formatDKK(dbTotal)} kr</p>
              <div className="flex gap-4 mt-2 text-xs text-white/40">
                <span>Produkt: {formatDKK(dbProd)} kr</span>
                <span>Montering: {formatDKK(dbMont)} kr</span>
              </div>
            </div>
            <div className="bg-[#151515] rounded-xl p-5 border border-white/5">
              <p className="text-xs text-white/50 mb-1">Udgifter i alt</p>
              <p className="text-2xl font-bold text-[#EF5350]">{formatDKK(udgifterTotal)} kr</p>
            </div>
            <div className="bg-[#151515] rounded-xl p-5 border border-white/5">
              <p className="text-xs text-white/50 mb-1">Overskud</p>
              <p className={`text-2xl font-bold ${profit >= 0 ? 'text-[#2E7D32]' : 'text-[#EF5350]'}`}>{formatDKK(profit)} kr</p>
              <p className="text-xs text-white/30 mt-1">Margin: {dbTotal > 0 ? ((profit / dbTotal) * 100).toFixed(1) : 0}%</p>
            </div>
          </div>

          {/* Detailed breakdown */}
          <div className="bg-[#151515] rounded-xl p-5 border border-white/5">
            <h3 className="text-sm font-medium text-white/80 mb-4">Resultatopgørelse</h3>
            <div className="space-y-1">
              {/* Indtægter */}
              <div className="flex justify-between items-center py-2 border-b border-white/5">
                <span className="text-sm text-white/70">DB Produkt</span>
                <span className="text-sm text-[#2E7D32] font-medium">{formatDKK(dbProd)} kr</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-white/10">
                <span className="text-sm text-white/70">DB Montering</span>
                <span className="text-sm text-[#2E7D32] font-medium">{formatDKK(dbMont)} kr</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-white/20 bg-white/[0.02] px-2 rounded">
                <span className="text-sm text-white font-semibold">Dækning i alt</span>
                <span className="text-sm text-[#2E7D32] font-bold">{formatDKK(dbTotal)} kr</span>
              </div>

              {/* Udgifter */}
              <div className="pt-3" />
              {udgiftsPoster.map((post, i) => (
                <div key={i} className="flex justify-between items-center py-2 border-b border-white/5">
                  <span className="text-sm text-white/70">
                    {post.label}
                    {post.dummy && <span className="ml-2 text-xs text-white/20 italic">DEMO</span>}
                  </span>
                  <span className="text-sm text-[#EF5350] font-medium">-{formatDKK(post.value)} kr</span>
                </div>
              ))}
              <div className="flex justify-between items-center py-2 border-b border-white/20 bg-white/[0.02] px-2 rounded">
                <span className="text-sm text-white font-semibold">Udgifter i alt</span>
                <span className="text-sm text-[#EF5350] font-bold">-{formatDKK(udgifterTotal)} kr</span>
              </div>

              {/* Resultat */}
              <div className="pt-3" />
              <div className={`flex justify-between items-center py-3 px-3 rounded-lg ${profit >= 0 ? 'bg-[#2E7D32]/10 border border-[#2E7D32]/20' : 'bg-[#EF5350]/10 border border-[#EF5350]/20'}`}>
                <span className="text-base text-white font-bold">Overskud</span>
                <span className={`text-base font-bold ${profit >= 0 ? 'text-[#2E7D32]' : 'text-[#EF5350]'}`}>{formatDKK(profit)} kr</span>
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (activeTab === 'kundetilfredshed') {
      const dummyTrustpilot = [
        { month: 'Sep 25', antal: 8, gns: 4.6 },
        { month: 'Okt 25', antal: 12, gns: 4.8 },
        { month: 'Nov 25', antal: 6, gns: 4.2 },
        { month: 'Dec 25', antal: 10, gns: 4.7 },
        { month: 'Jan 26', antal: 9, gns: 4.5 },
        { month: 'Feb 26', antal: 14, gns: 4.9 },
        { month: 'Mar 26', antal: 7, gns: 4.3 },
      ];
      const dummyGoogle = [
        { month: 'Sep 25', antal: 5, gns: 4.4 },
        { month: 'Okt 25', antal: 8, gns: 4.6 },
        { month: 'Nov 25', antal: 4, gns: 4.0 },
        { month: 'Dec 25', antal: 7, gns: 4.8 },
        { month: 'Jan 26', antal: 6, gns: 4.3 },
        { month: 'Feb 26', antal: 11, gns: 4.7 },
        { month: 'Mar 26', antal: 5, gns: 4.5 },
      ];
      const tpTotal = dummyTrustpilot.reduce((s, d) => s + d.antal, 0);
      const tpAvg = dummyTrustpilot.reduce((s, d) => s + d.gns * d.antal, 0) / tpTotal;
      const gTotal = dummyGoogle.reduce((s, d) => s + d.antal, 0);
      const gAvg = dummyGoogle.reduce((s, d) => s + d.gns * d.antal, 0) / gTotal;

      // Dummy star distribution
      const tpStars = { 5: 38, 4: 18, 3: 6, 2: 2, 1: 2 };
      const gStars = { 5: 24, 4: 12, 3: 5, 2: 3, 1: 2 };

      const StarRating = ({ rating }) => (
        <div className="flex gap-0.5">
          {[1,2,3,4,5].map(i => (
            <svg key={i} viewBox="0 0 20 20" className="w-5 h-5">
              <path d="M10 1l2.39 4.84 5.34.78-3.87 3.77.91 5.33L10 13.15l-4.77 2.51.91-5.33L2.27 6.56l5.34-.78L10 1z"
                fill={i <= Math.round(rating) ? '#FFB300' : 'rgba(255,255,255,0.1)'}
              />
            </svg>
          ))}
        </div>
      );

      const StarDistribution = ({ stars, total, accentColor }) => (
        <div className="space-y-1.5 mt-4">
          {[5,4,3,2,1].map(s => {
            const count = stars[s] || 0;
            const pct = total > 0 ? (count / total) * 100 : 0;
            return (
              <div key={s} className="flex items-center gap-2">
                <span className="text-xs text-white/50 w-3 text-right">{s}</span>
                <svg viewBox="0 0 20 20" className="w-3.5 h-3.5 flex-shrink-0">
                  <path d="M10 1l2.39 4.84 5.34.78-3.87 3.77.91 5.33L10 13.15l-4.77 2.51.91-5.33L2.27 6.56l5.34-.78L10 1z" fill="#FFB300" />
                </svg>
                <div className="flex-1 h-2.5 bg-white/5 rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: accentColor }} />
                </div>
                <span className="text-xs text-white/40 w-6 text-right">{count}</span>
              </div>
            );
          })}
        </div>
      );

      return (
        <div className="space-y-6">
          {/* Overview cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Trustpilot */}
            <div className="bg-[#151515] rounded-xl p-5 border border-white/5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-[#00B67A] rounded-lg flex items-center justify-center">
                    <svg viewBox="0 0 24 24" className="w-5 h-5 text-white" fill="currentColor"><path d="M12 1l2.89 5.86 6.46.94-4.68 4.56 1.1 6.44L12 15.77l-5.77 3.03 1.1-6.44L2.65 7.8l6.46-.94z"/></svg>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-white">Trustpilot</h3>
                    <span className="text-xs text-white/30 italic">DEMO-DATA</span>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-[#00B67A]">{tpAvg.toFixed(1)}</p>
                  <p className="text-xs text-white/40">{tpTotal} anmeldelser</p>
                </div>
              </div>
              <StarRating rating={tpAvg} />
              <StarDistribution stars={tpStars} total={tpTotal} accentColor="#00B67A" />
              <div className="mt-4">
                <ResponsiveContainer width="100%" height={180}>
                  <ComposedChart data={dummyTrustpilot} margin={{ top: 5, right: 5, left: -15, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                    <XAxis dataKey="month" tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 10 }} />
                    <YAxis yAxisId="left" tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 10 }} />
                    <YAxis yAxisId="right" orientation="right" domain={[0, 5]} tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 10 }} />
                    <Tooltip contentStyle={{ backgroundColor: '#1A1A1A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff' }} />
                    <Bar yAxisId="left" dataKey="antal" name="Antal" fill="#00B67A" fillOpacity={0.6} radius={[3, 3, 0, 0]} />
                    <Line yAxisId="right" type="monotone" dataKey="gns" name="Gns. score" stroke="#FFB300" strokeWidth={2} dot={{ fill: '#FFB300', r: 3 }} isAnimationActive={false} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Google */}
            <div className="bg-[#151515] rounded-xl p-5 border border-white/5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-[#4285F4] rounded-lg flex items-center justify-center">
                    <svg viewBox="0 0 24 24" className="w-5 h-5 text-white" fill="currentColor"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A10.96 10.96 0 0 0 1 12c0 1.77.42 3.45 1.18 4.93l3.66-2.84z M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-white">Google Anmeldelser</h3>
                    <span className="text-xs text-white/30 italic">DEMO-DATA</span>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-[#4285F4]">{gAvg.toFixed(1)}</p>
                  <p className="text-xs text-white/40">{gTotal} anmeldelser</p>
                </div>
              </div>
              <StarRating rating={gAvg} />
              <StarDistribution stars={gStars} total={gTotal} accentColor="#4285F4" />
              <div className="mt-4">
                <ResponsiveContainer width="100%" height={180}>
                  <ComposedChart data={dummyGoogle} margin={{ top: 5, right: 5, left: -15, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                    <XAxis dataKey="month" tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 10 }} />
                    <YAxis yAxisId="left" tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 10 }} />
                    <YAxis yAxisId="right" orientation="right" domain={[0, 5]} tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 10 }} />
                    <Tooltip contentStyle={{ backgroundColor: '#1A1A1A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff' }} />
                    <Bar yAxisId="left" dataKey="antal" name="Antal" fill="#4285F4" fillOpacity={0.6} radius={[3, 3, 0, 0]} />
                    <Line yAxisId="right" type="monotone" dataKey="gns" name="Gns. score" stroke="#FFB300" strokeWidth={2} dot={{ fill: '#FFB300', r: 3 }} isAnimationActive={false} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (activeTab === 'arbejdstid') {
      const tPeriods = tidsfordelingData?.period_data || [];
      const tTotals = tidsfordelingData?.totals || {};
      const tPct = tidsfordelingData?.percentages || {};
      
      const activityKeys = [
        { key: 'salgsbesog', label: 'Salgsbesøg', color: '#1E6F7A' },
        { key: 'montering', label: 'Montering', color: '#E65100' },
        { key: 'korsel', label: 'Kørsel', color: '#7B1FA2' },
        { key: 'administration', label: 'Administration', color: '#1565C0' },
        { key: 'uden_type', label: 'Uden type', color: '#546E7A' },
        { key: 'reklamation', label: 'Reklamation', color: '#C62828' },
        { key: 'andet_akut', label: 'Andet akut', color: '#F9A825' },
        { key: 'reserveret_tid', label: 'Reserveret tid', color: '#00897B' },
        { key: 'kontorarbejde', label: 'Kontorarbejde', color: '#5C6BC0' },
        { key: 'pakke_bil', label: 'Pakke bil', color: '#8D6E63' },
      ];
      
      // Pie chart data
      const pieData = activityKeys
        .filter(a => (tTotals[a.key] || 0) > 0)
        .map(a => ({
          name: a.label,
          value: tTotals[a.key] || 0,
          pct: tPct[a.key] || 0,
          color: a.color
        }));

      const CustomTooltipStacked = ({ active, payload, label }) => {
        if (!active || !payload) return null;
        return (
          <div className="bg-[#1A1A1A] border border-white/10 rounded-lg p-3 text-xs">
            <p className="text-white font-medium mb-1">{label}</p>
            {payload.filter(p => p.value > 0).map((p, i) => (
              <p key={i} style={{ color: p.color }} className="flex justify-between gap-4">
                <span>{p.name}</span>
                <span className="font-mono">{p.value.toFixed(1)} t</span>
              </p>
            ))}
            <p className="text-white/60 border-t border-white/10 mt-1 pt-1 flex justify-between gap-4">
              <span>Total</span>
              <span className="font-mono">{payload.reduce((s, p) => s + (p.value || 0), 0).toFixed(1)} t</span>
            </p>
          </div>
        );
      };

      return (
        <div className="space-y-6" data-testid="arbejdstid-tab">
          {/* Overview cards */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            {activityKeys.slice(0, 5).map(a => (
              <div key={a.key} className="bg-[#151515] rounded-xl p-4 border border-white/5">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: a.color }} />
                  <span className="text-white/50 text-xs">{a.label}</span>
                </div>
                <p className="text-white text-lg font-semibold">{formatDKK(Math.round(tTotals[a.key] || 0))} t</p>
                <p className="text-white/40 text-xs">{tPct[a.key] || 0}%</p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Stacked bar chart */}
            <div className="lg:col-span-2 bg-[#151515] rounded-xl p-5 border border-white/5" data-testid="tidsfordeling-chart">
              <h3 className="text-white text-sm font-medium mb-4">Tidsfordeling pr. måned (timer)</h3>
              <div className="h-[400px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={tPeriods} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                    <XAxis dataKey="period_label" tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 11 }} />
                    <YAxis tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 11 }} />
                    <Tooltip content={<CustomTooltipStacked />} />
                    {activityKeys.map((a, i) => (
                      <Bar 
                        key={a.key}
                        dataKey={a.key}
                        name={a.label}
                        stackId="a"
                        fill={a.color}
                        radius={i === activityKeys.length - 1 ? [3, 3, 0, 0] : [0, 0, 0, 0]}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Pie chart */}
            <div className="bg-[#151515] rounded-xl p-5 border border-white/5" data-testid="tidsfordeling-pie">
              <h3 className="text-white text-sm font-medium mb-4">Samlet fordeling</h3>
              <div className="h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {pieData.map((entry, i) => (
                        <Cell key={i} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value, name) => [`${value.toFixed(1)} timer`, name]}
                      contentStyle={{ backgroundColor: '#1A1A1A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff', fontSize: '12px' }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              {/* Legend */}
              <div className="space-y-1.5 mt-2">
                {pieData.map((d, i) => (
                  <div key={i} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: d.color }} />
                      <span className="text-white/70">{d.name}</span>
                    </div>
                    <span className="text-white/50 font-mono">{d.pct}%</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Ledige timer charts */}
          <div data-testid="ledige-timer-section">
            {/* Per week chart */}
            <div className="bg-[#151515] rounded-xl p-5 border border-white/5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white text-sm font-medium">Ledige timer pr. uge</h3>
                <span className="text-white/40 text-xs">gap_time_min_60</span>
              </div>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={ledigeTimerData?.per_week || []} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                    <XAxis dataKey="label" tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 11 }} />
                    <YAxis tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 11 }} />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#1A1A1A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff', fontSize: '12px' }}
                      formatter={(value) => [`${value} ledige timer`, 'Ledige']}
                      labelFormatter={(label) => label}
                    />
                    <Bar dataKey="free_hours" name="Ledige timer" fill="#F44336" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-3 text-center">
                <span className="text-white/50 text-xs">Total: <span className="text-white font-medium">{ledigeTimerData?.total_free_hours || 0}</span> ledige timer i perioden</span>
              </div>
            </div>
          </div>
          {/* Fravær section */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="fravaer-section">
            {/* Stacked bar chart */}
            <div className="lg:col-span-2 bg-[#151515] rounded-xl p-5 border border-white/5">
              <h3 className="text-white text-sm font-medium mb-4">Fravær pr. måned (dage)</h3>
              <div className="h-[350px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={fravaerData?.period_data || []} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                    <XAxis dataKey="period_label" tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 11 }} />
                    <YAxis tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 11 }} allowDecimals={false} />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#1A1A1A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff', fontSize: '12px' }}
                      formatter={(value, name) => [`${value} dage`, name]}
                    />
                    <Bar dataKey="sygdom" name="Sygdom" stackId="a" fill="#E53935" />
                    <Bar dataKey="ferie" name="Ferie" stackId="a" fill="#1E88E5" />
                    <Bar dataKey="barns_sygedag" name="Barnets 1. sygedag" stackId="a" fill="#FB8C00" />
                    <Bar dataKey="barsel" name="Barsel" stackId="a" fill="#8E24AA" />
                    <Bar dataKey="afspadsering" name="Afspadsering" stackId="a" fill="#43A047" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Summary with donut */}
            <div className="bg-[#151515] rounded-xl p-5 border border-white/5">
              <h3 className="text-white text-sm font-medium mb-4">Samlet fravær</h3>
              <div className="h-[220px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={[
                        { name: 'Sygdom', value: fravaerData?.totals?.sygdom || 0, color: '#E53935' },
                        { name: 'Ferie', value: fravaerData?.totals?.ferie || 0, color: '#1E88E5' },
                        { name: 'Barnets 1. sygedag', value: fravaerData?.totals?.barns_sygedag || 0, color: '#FB8C00' },
                        { name: 'Barsel', value: fravaerData?.totals?.barsel || 0, color: '#8E24AA' },
                        { name: 'Afspadsering', value: fravaerData?.totals?.afspadsering || 0, color: '#43A047' },
                      ].filter(d => d.value > 0)}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {[
                        { color: '#E53935' },
                        { color: '#1E88E5' },
                        { color: '#FB8C00' },
                        { color: '#8E24AA' },
                        { color: '#43A047' },
                      ].filter((_, i) => {
                        const keys = ['sygdom', 'ferie', 'barns_sygedag', 'barsel', 'afspadsering'];
                        return (fravaerData?.totals?.[keys[i]] || 0) > 0;
                      }).map((entry, i) => (
                        <Cell key={i} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value, name) => [`${value} dage`, name]}
                      contentStyle={{ backgroundColor: '#1A1A1A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff', fontSize: '12px' }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="text-center mb-4">
                <span className="text-white/50 text-xs">Total: <span className="text-white text-lg font-semibold">{fravaerData?.total_days || 0}</span> fraværsdage</span>
              </div>
              <div className="space-y-2">
                {[
                  { key: 'sygdom', label: 'Sygdom', color: '#E53935' },
                  { key: 'ferie', label: 'Ferie', color: '#1E88E5' },
                  { key: 'barns_sygedag', label: 'Barnets 1. sygedag', color: '#FB8C00' },
                  { key: 'barsel', label: 'Barsel', color: '#8E24AA' },
                  { key: 'afspadsering', label: 'Afspadsering', color: '#43A047' },
                ].filter(a => (fravaerData?.totals?.[a.key] || 0) > 0).map(a => (
                  <div key={a.key} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: a.color }} />
                      <span className="text-white/70">{a.label}</span>
                    </div>
                    <span className="text-white/50 font-mono">{fravaerData?.totals?.[a.key] || 0} dage ({fravaerData?.percentages?.[a.key] || 0}%)</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="bg-[#1A1A1A] rounded-lg p-8 text-center">
        <p className="text-white/40">Denne fane er under udvikling</p>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#121212]" data-testid="konsulenter-page">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-[#121212]/95 backdrop-blur-md border-b border-white/5">
        <div className="max-w-[1920px] mx-auto px-6 py-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Navigation />
              <h1 className="text-xl font-semibold text-white">Konsulenter</h1>
            </div>
            <div className="flex items-center gap-3">
              <button onClick={handleRefresh} disabled={refreshing} className="p-2 bg-[#1A1A1A] border border-white/10 text-white/60 hover:text-white rounded-lg transition-colors disabled:opacity-50">
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              </button>
              <button onClick={handleLogout} className="p-2 bg-[#1A1A1A] border border-white/10 text-white/60 hover:text-[#B3261E] rounded-lg transition-colors">
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-[1920px] mx-auto px-6 py-6 space-y-6">
        
        {/* Filter Bar */}
        <div className="flex flex-wrap items-center gap-4 bg-[#1A1A1A] rounded-lg p-4">
          {/* Consultant Dropdown */}
          <div className="relative">
            <select
              value={selectedConsultant}
              onChange={(e) => setSelectedConsultant(e.target.value)}
              className="appearance-none bg-[#252525] border border-white/10 text-white text-sm px-4 py-2 pr-10 rounded-lg focus:outline-none focus:border-[#1E6F7A] cursor-pointer"
            >
              <option value="all">Alle konsulenter</option>
              {consultants.map((c) => (
                <option key={c.email} value={c.email}>{c.name}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40 pointer-events-none" />
          </div>

          {/* Date Range */}
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="bg-[#252525] border border-white/10 text-white text-sm px-3 py-2 rounded-lg focus:outline-none focus:border-[#1E6F7A]"
              data-testid="konsulent-start-date"
            />
            <span className="text-white/40">-</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="bg-[#252525] border border-white/10 text-white text-sm px-3 py-2 rounded-lg focus:outline-none focus:border-[#1E6F7A]"
              data-testid="konsulent-end-date"
            />
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-white/10 pb-2">
          {[
            { id: 'salgstal', label: 'Salgstal' },
            { id: 'profit', label: 'Profit' },
            { id: 'arbejdstid', label: 'Arbejdstid' },
            { id: 'kundetilfredshed', label: 'Kundetilfredshed' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                activeTab === tab.id
                  ? 'bg-[#1E6F7A] text-white'
                  : 'text-white/60 hover:text-white hover:bg-white/5'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-white/60">Indlæser data...</div>
          </div>
        ) : (
          renderTabContent()
        )}

      </main>

      {/* Footer */}
      <footer className="border-t border-white/5 py-4 mt-8">
        <div className="max-w-[1920px] mx-auto px-6">
          <p className="text-center text-xs text-white/60">
            Luxsun Nordic · Konsulenter · {new Date().getFullYear()}
          </p>
        </div>
      </footer>
    </div>
  );
};
